import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from 'react-router-dom'
import { lazy, Suspense, type ReactNode } from 'react'
import Spinner from '@/components/common/Spinner'

// ── Lazy-loaded pages ──────────────────────────────────────────
const HomePage = lazy(() => import('@/pages/HomePage'))
const BrowsePage = lazy(() => import('@/pages/renter/BrowsePage'))
const SpaceDetailPage = lazy(() => import('@/pages/renter/SpaceDetailPage'))
const BookingFlowPage = lazy(() => import('@/pages/renter/BookingFlowPage'))
const BookingsPage = lazy(() => import('@/pages/renter/BookingsPage'))
const RenterBookingDetailPage = lazy(() => import('@/pages/renter/RenterBookingDetailPage'))
const HostDashboardPage = lazy(() => import('@/pages/host/HostDashboardPage'))
const HostListingsPage = lazy(() => import('@/pages/host/HostListingsPage'))
const CreateListingPage = lazy(() => import('@/pages/host/CreateListingPage'))
const EditListingPage = lazy(() => import('@/pages/host/EditListingPage'))
const HostBookingDetailPage = lazy(() => import('@/pages/host/HostBookingDetailPage'))
const MessagesPage = lazy(() => import('@/pages/shared/MessagesPage'))
const ChatPage = lazy(() => import('@/pages/shared/ChatPage'))
const ProfilePage = lazy(() => import('@/pages/shared/ProfilePage'))
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const BecomeAHostPage = lazy(() => import('@/pages/BecomeAHostPage'))
const PricingPage = lazy(() => import('@/pages/PricingPage'))
const HowItWorksPage = lazy(() => import('@/pages/HowItWorksPage'))
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'))
const HelpPage = lazy(() => import('@/pages/HelpPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))
const TermsPage = lazy(() => import('@/pages/TermsPage'))
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage'))
const PaymentCancelledPage = lazy(() => import('@/pages/PaymentCancelledPage'))

// ── Page Loading Fallback ──────────────────────────────────────
function PageLoader() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
      }}
    >
      <Spinner size="lg" style={{ color: 'var(--color-accent)' }} />
    </div>
  )
}

function S({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

// ── Root layout (scroll restoration) ──────────────────────────
function RootLayout() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  )
}

// ── Router ─────────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <S><HomePage /></S> },
      // Auth
      { path: '/login', element: <S><LoginPage /></S> },
      { path: '/signup', element: <S><SignupPage /></S> },
      { path: '/forgot-password', element: <S><ForgotPasswordPage /></S> },
      { path: '/reset-password', element: <S><ResetPasswordPage /></S> },
      // Renter
      { path: '/browse', element: <S><BrowsePage /></S> },
      { path: '/space/:id', element: <S><SpaceDetailPage /></S> },
      { path: '/booking/:id', element: <S><BookingFlowPage /></S> },
      { path: '/bookings', element: <S><BookingsPage /></S> },
      { path: '/bookings/:id', element: <S><RenterBookingDetailPage /></S> },
      // Host
      { path: '/host/dashboard', element: <S><HostDashboardPage /></S> },
      { path: '/host/listings', element: <S><HostListingsPage /></S> },
      { path: '/host/listings/new', element: <S><CreateListingPage /></S> },
      { path: '/host/listings/:id/edit', element: <S><EditListingPage /></S> },
      { path: '/host/bookings/:id', element: <S><HostBookingDetailPage /></S> },
      // Shared (auth required)
      { path: '/messages', element: <S><MessagesPage /></S> },
      { path: '/messages/:id', element: <S><ChatPage /></S> },
      { path: '/profile', element: <S><ProfilePage /></S> },
      // Payment outcomes
      { path: '/payment-success', element: <S><PaymentSuccessPage /></S> },
      { path: '/payment-cancelled', element: <S><PaymentCancelledPage /></S> },
      // Public marketing
      { path: '/become-a-host', element: <S><BecomeAHostPage /></S> },
      { path: '/pricing', element: <S><PricingPage /></S> },
      { path: '/how-it-works', element: <S><HowItWorksPage /></S> },
      { path: '/categories', element: <S><CategoriesPage /></S> },
      { path: '/help', element: <S><HelpPage /></S> },
      { path: '/privacy', element: <S><PrivacyPage /></S> },
      { path: '/terms', element: <S><TermsPage /></S> },
      // Legacy redirects
      { path: '/listing/:id', element: <Navigate to="/space/:id" replace /> },
      { path: '/host', element: <Navigate to="/host/dashboard" replace /> },
      { path: '/register', element: <Navigate to="/signup" replace /> },
      // 404
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
